import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Clock3, Copy, ExternalLink, FileText, Info, Loader2 } from 'lucide-react';
import {
  EvvMissedPromptVisit,
  EvvReconciliationRun,
  EvvReconciliationRunDetail,
  EvvReconciliationService,
  EvvReconciliationSummary,
  EvvRunResponse,
  EvvUsageScore,
  bucketLabel
} from '../../../services/evv/reconciliation.service';
import { useFeature } from '../../../hooks/useFeature';
import { usePermission } from '../../../hooks/usePermission';
import EvvUploadPanel from './evv/EvvUploadPanel';
import EvvRunItems from './evv/EvvRunItems';
import EvvOverrideForm from './evv/EvvOverrideForm';
import EvvPatientMapPanel from './evv/EvvPatientMapPanel';
import { EVV_OVERRIDE, EVV_WRITE, HHAX_URL, errorMessage, formatDateTime } from './evv/evvUi';

interface EvvReconciliationProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
}

const USAGE_SCORE_HELP = 'HHSC and the MCOs review EVV usage each quarter: the share of EVV visits verified automatically, '
  + 'without manual visit maintenance. This estimate counts reconciled EVV-required visits in the last 90 days that '
  + 'matched HHAX by schedule reference or clock times with no exception, override or manual link. It is informational '
  + 'and never blocks billing.';

const TILES: Array<{ key: keyof NonNullable<EvvReconciliationRun['results']>; bucket: string; tone: string }> = [
  { key: 'matched', bucket: 'MATCHED', tone: 'emerald' },
  { key: 'durationMismatch', bucket: 'DURATION_MISMATCH', tone: 'amber' },
  { key: 'hhaxOnly', bucket: 'HHAX_ONLY', tone: 'rose' },
  { key: 'blackbirdOnly', bucket: 'BLACKBIRD_ONLY', tone: 'sky' },
  { key: 'exceptions', bucket: 'HHAX_UNVERIFIED', tone: 'orange' },
  { key: 'manualReview', bucket: 'MANUAL_REVIEW', tone: 'violet' },
];

const TONE: Record<string, string> = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
  rose: 'border-rose-200 bg-rose-50 text-rose-800',
  sky: 'border-sky-200 bg-sky-50 text-sky-800',
  orange: 'border-orange-200 bg-orange-50 text-orange-800',
  violet: 'border-violet-200 bg-violet-50 text-violet-800',
};

function runSummary(run: EvvReconciliationRun): string {
  const r = run.results || {};
  const range = run.csvActualDateRange ? `${run.csvActualDateRange.from} to ${run.csvActualDateRange.to}` : 'no dates';
  return `${range} · ${r.matched ?? 0} matched · ${r.durationMismatch ?? 0} differ · ${r.hhaxOnly ?? 0} HHAX only · ${r.blackbirdOnly ?? 0} Pelora only`
    + `${r.manualReview ? ` · ${r.manualReview} review` : ''}${r.alreadyVerified ? ` · ${r.alreadyVerified} already verified` : ''}`;
}

const EvvReconciliation: React.FC<EvvReconciliationProps> = ({ expanded, toggleExpanded }) => {
  const interventionEnabled = useFeature('evvFlagIntervention');
  const canWrite = usePermission(EVV_WRITE);
  const canOverride = usePermission(EVV_OVERRIDE);
  const [summary, setSummary] = useState<EvvReconciliationSummary | null>(null);
  const [runs, setRuns] = useState<EvvReconciliationRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<EvvReconciliationRunDetail | null>(null);
  const [usageScore, setUsageScore] = useState<EvvUsageScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [globalSuccess, setGlobalSuccess] = useState<string | null>(null);
  const [flaggedNote, setFlaggedNote] = useState<Record<string, string>>({});

  const loadRun = useCallback(async (reconciliationId: string) => {
    setLoadingRun(true);
    try {
      setSelectedRun(await EvvReconciliationService.getRun(reconciliationId));
    } catch (error) {
      setGlobalError(errorMessage(error, 'Failed to load the run'));
    } finally {
      setLoadingRun(false);
    }
  }, []);

  const loadAll = useCallback(async (selectId?: string) => {
    setLoading(true);
    setGlobalError(null);
    try {
      const [nextSummary, nextRuns, nextScore] = await Promise.all([
        EvvReconciliationService.getSummary(),
        EvvReconciliationService.listRuns(50),
        EvvReconciliationService.getUsageScore(),
      ]);
      setSummary(nextSummary);
      setRuns(nextRuns);
      setUsageScore(nextScore);
      const target = selectId || nextRuns[0]?.reconciliationId;
      if (target) await loadRun(target);
    } catch (error) {
      setGlobalError(errorMessage(error, 'Failed to load EVV reconciliation'));
    } finally {
      setLoading(false);
    }
  }, [loadRun]);

  useEffect(() => {
    if (expanded) loadAll();
  }, [expanded, loadAll]);

  // Sidebar badge (EVV-18)
  useEffect(() => {
    if (summary) window.dispatchEvent(new CustomEvent('evv-missed-prompt-count', { detail: summary.missedPromptCount }));
  }, [summary]);

  const onCompleted = async (run: EvvRunResponse) => {
    setGlobalSuccess(`Reconciliation complete: ${run.results.matched + run.results.durationMismatch + run.results.upgraded} verified, ${run.unresolvedCount} need a decision.`);
    await loadAll(run.reconciliationId);
  };

  const onChanged = async (message: string) => {
    setGlobalSuccess(message);
    setGlobalError(null);
    await loadAll(selectedRun?.reconciliationId);
  };

  const intervene = async (visit: EvvMissedPromptVisit, action: 'MARK_RESOLVED' | 'MARK_UNABLE_TO_RESOLVE') => {
    try {
      await EvvReconciliationService.interveneFlaggedVisit(visit.visitId, action, flaggedNote[visit.visitId]);
      await onChanged(`Intervention saved for visit ${visit.visitId}`);
    } catch (error) {
      setGlobalError(errorMessage(error, 'Failed to save the intervention'));
    }
  };

  const exportUnresolved = async () => {
    if (!selectedRun) return;
    try {
      await EvvReconciliationService.downloadUnresolvedCsv(selectedRun.reconciliationId);
    } catch (error) {
      setGlobalError(errorMessage(error, 'Failed to export'));
    }
  };

  const range = summary?.recommendedRange;
  const rangeText = range ? `${range.from} to ${range.to}` : null;

  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-white">
      <button type="button" onClick={toggleExpanded} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <div>
          <h3 className="text-base font-semibold text-slate-800">EVV Reconciliation</h3>
          <p className="text-xs text-slate-500">Match the HHAX Billing Exceptions Export to Pelora visits and set billable EVV minutes.</p>
        </div>
        <Clock3 size={16} className="text-slate-500" />
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-slate-200 px-4 py-4">
          {/* EVV-02 / EVV-04 / EVV-18 header */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4" data-testid="evv-summary">
            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs text-slate-500">HHAX provider ID</p>
              <p className="text-sm font-semibold text-slate-800">{summary?.hhaxProviderId || 'Not set (Agency Setup → EVV)'}</p>
              <p className="mt-1 text-xs text-slate-500">EVV System Administrator</p>
              <p className="text-xs text-slate-700">
                {summary?.evvSystemAdmin?.name || 'Not set'}
                {summary?.evvSystemAdmin?.email && (
                  <> · <a className="text-sky-700 underline" href={`mailto:${summary.evvSystemAdmin.email}`}>{summary.evvSystemAdmin.email}</a></>
                )}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Pending EVV visits</p>
              <p className="text-lg font-semibold text-slate-800" data-testid="evv-pending-count">{summary?.pendingCount ?? '—'}</p>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Missed clock-in/out prompts</p>
              <p className="text-lg font-semibold text-slate-800" data-testid="evv-missed-count">{summary?.missedPromptCount ?? '—'}</p>
              <p className="text-xs text-slate-500">Pending visits only; informational</p>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Recommended HHAX export range</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="evv-recommended-range">{rangeText || 'No pending visits'}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                {rangeText && (
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(rangeText)}
                    className="inline-flex items-center rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <Copy size={12} className="mr-1" /> Copy
                  </button>
                )}
                <a href={HHAX_URL} target="_blank" rel="noreferrer" className="inline-flex items-center rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50">
                  HHAeXchange <ExternalLink size={12} className="ml-1" />
                </a>
              </div>
            </div>
          </div>

          {globalError && <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700" role="alert">{globalError}</div>}
          {globalSuccess && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">{globalSuccess}</div>}

          {canWrite && (
            <EvvUploadPanel
              defaultTolerance={summary?.toleranceMinutes ?? 15}
              savedMapping={summary?.columnMapping || {}}
              onCompleted={onCompleted}
            />
          )}

          {/* REQ-EVV-21 usage score (MVP since Phase 10) */}
          <div className="rounded-md border border-slate-200 p-3" data-testid="evv-usage-score">
            <p className="flex items-center gap-1 text-sm font-medium text-slate-700">
              EVV usage score (estimate, last 90 days)
              <span title={USAGE_SCORE_HELP} aria-label={USAGE_SCORE_HELP}><Info size={14} className="text-slate-400" /></span>
            </p>
            {!usageScore ? (
              <p className="text-sm text-slate-500">{loading ? 'Loading…' : 'No score yet.'}</p>
            ) : !usageScore.scoreAvailable ? (
              <p className="mt-1 text-sm text-slate-600">{usageScore.noRunMessage}</p>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
                <div className="rounded-md bg-slate-50 p-2">
                  <p className="text-xs text-slate-500">Score</p>
                  <p className="text-base font-semibold text-slate-800">{usageScore.projectedScorePct}%</p>
                </div>
                <div className="rounded-md bg-slate-50 p-2">
                  <p className="text-xs text-slate-500">Auto-verified / reconciled</p>
                  <p className="text-base font-semibold text-slate-800">{usageScore.autoVerifiedVisits} / {usageScore.reconciledVisits}</p>
                </div>
                <div className="rounded-md bg-slate-50 p-2">
                  <p className="text-xs text-slate-500">Exceptions</p>
                  <p className="text-base font-semibold text-slate-800">{usageScore.exceptionVisits}</p>
                </div>
                <div className="rounded-md bg-slate-50 p-2">
                  <p className="text-xs text-slate-500">Pending</p>
                  <p className="text-base font-semibold text-slate-800">{usageScore.pendingReconciliationVisits}</p>
                </div>
              </div>
            )}
            {usageScore?.estimateLabel && <p className="mt-2 text-xs text-slate-500">{usageScore.estimateLabel}</p>}
          </div>

          {/* EVV-17 run history */}
          <div className="rounded-md border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">Run history</p>
              {loading && <Loader2 size={14} className="animate-spin text-slate-500" />}
            </div>
            {runs.length === 0 ? (
              <div className="flex items-start gap-2 rounded-md bg-slate-50 p-2 text-sm text-slate-600">
                <AlertCircle size={14} className="mt-0.5 text-amber-600" />
                <span>No reconciliation runs yet.</span>
              </div>
            ) : (
              <div className="space-y-2" data-testid="evv-run-history">
                {runs.map((run) => (
                  <button
                    key={run.reconciliationId}
                    type="button"
                    onClick={() => loadRun(run.reconciliationId)}
                    className={`w-full rounded-md border px-3 py-2 text-left ${selectedRun?.reconciliationId === run.reconciliationId ? 'border-slate-400 bg-slate-100' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                  >
                    <div className="text-xs font-medium text-slate-700">
                      {formatDateTime(run.runAt)} · {run.runByName || run.runBy || 'Unknown user'} · {run.sourceFileName || 'HHAX export'}
                    </div>
                    <div className="text-xs text-slate-500">{runSummary(run)}</div>
                    {(run.unresolvedCount ?? 0) > 0 && <div className="text-xs text-amber-700">{run.unresolvedCount} need a decision</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedRun && (
            <div className="rounded-md border border-slate-200 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-700">Run of {formatDateTime(selectedRun.runAt)}</p>
                <div className="flex items-center gap-2">
                  {loadingRun && <Loader2 size={14} className="animate-spin text-slate-500" />}
                  <button
                    type="button"
                    onClick={exportUnresolved}
                    className="inline-flex items-center rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <FileText size={12} className="mr-1" /> Export open items
                  </button>
                </div>
              </div>
              <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-6">
                {TILES.map((tile) => (
                  <div key={tile.key} className={`rounded-md border p-2 ${TONE[tile.tone]}`}>
                    <p className="text-xs font-medium">{bucketLabel(tile.bucket)}</p>
                    <p className="text-lg font-semibold">{selectedRun.results?.[tile.key] ?? 0}</p>
                  </div>
                ))}
              </div>
              {(selectedRun.parseFailedCount ?? 0) > 0 && (
                <p className="mb-2 text-xs text-amber-700">{selectedRun.parseFailedCount} CSV row(s) could not be read and were skipped.</p>
              )}
              <EvvRunItems run={selectedRun} canWrite={canWrite} canOverride={canOverride} onChanged={onChanged} />
            </div>
          )}

          {/* EVV-18: always shown, count included even when 0 */}
          <div className="rounded-md border border-slate-200 p-3" data-testid="evv-missed-prompts">
            <p className="text-sm font-medium text-slate-700">Missed clock-in/out prompts ({summary?.missedPromptCount ?? 0})</p>
            {!summary || summary.missedPromptVisits.length === 0 ? (
              <p className="mt-1 text-sm text-slate-600">No Pending visits with a missed prompt.</p>
            ) : (
              <>
                <p className="mt-1 text-xs text-slate-600">
                  These Pending visits had no HHAX clock-in or clock-out confirmed in Pelora. Ask the nurse to complete the visit in
                  HHAeXchange before your next export.
                </p>
                <div className="mt-2 space-y-2">
                  {summary.missedPromptVisits.map((visit) => (
                    <div key={visit.visitId} className="rounded-md border border-slate-200 p-2">
                      <p className="text-xs font-semibold text-slate-700">Visit {visit.visitId} · {visit.visitDate || 'date unknown'}</p>
                      <p className="text-xs text-slate-500">
                        Missed: {[visit.evvClockInFlag === 'missed_prompt' ? 'clock-in' : null, visit.evvClockOutFlag === 'missed_prompt' ? 'clock-out' : null].filter(Boolean).join(' and ')}
                        {visit.intervention?.status ? ` · last intervention: ${visit.intervention.status}` : ''}
                      </p>
                      {interventionEnabled && canWrite && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <input
                            className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
                            placeholder="Coordinator note (optional)"
                            value={flaggedNote[visit.visitId] || ''}
                            onChange={(e) => setFlaggedNote((prev) => ({ ...prev, [visit.visitId]: e.target.value }))}
                          />
                          <button type="button" onClick={() => intervene(visit, 'MARK_RESOLVED')} className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
                            Mark resolved
                          </button>
                          <button type="button" onClick={() => intervene(visit, 'MARK_UNABLE_TO_RESOLVE')} className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-xs text-rose-800">
                            Unable to resolve
                          </button>
                        </div>
                      )}
                      {canOverride && (
                        <div className="mt-2 flex justify-end">
                          <EvvOverrideForm patientId={visit.patientId} visitId={visit.visitId} onDone={onChanged} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {canWrite && <EvvPatientMapPanel />}
        </div>
      )}
    </div>
  );
};

export default EvvReconciliation;
